import { useBoardStore } from '../store/useBoardStore';
import { removeSuggestPinShape } from './applyBoardAiActions';
import { CONVERSATION_PIN_TYPE } from './createConversationPin';
import { confirmDeleteUserPins, deleteUserPins } from './deleteUserPins';
import { framesFromEditor } from './boardSnapshot';
import { buildBoardDocumentOps } from './buildBoardDocumentOps';
import {
  flushBoardDocumentOps,
  syncLocalBoardSnapshot,
} from './persistBoardDocumentOps';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import {
  enqueueDigestDeleteFromStore,
  withDigestDeleteListenersSuppressed,
} from './boardConstraintsDigest';
import {
  collectDigestDeleteTargets,
  expandSelectionForCanvasDelete,
} from './canvasDeleteSelection';
import { unmountShapesThen } from './pendingShapeUnmount';

const PIN_SHAPE_TYPES = new Set(['pin-card', 'suggest-pin', CONVERSATION_PIN_TYPE]);

export function isPinShape(shape) {
  return Boolean(shape && PIN_SHAPE_TYPES.has(shape.type));
}

function buildSaveFrames() {
  const store = useBoardStore.getState();
  return frames => store.saveFrames(Array.isArray(frames) ? frames : []);
}

async function removeConversationPinShape(editor, shapeId) {
  if (!editor || !shapeId) return;
  const before = extractBoardDocumentSnapshot(editor);
  unmountShapesThen([shapeId], () => {
    editor.run(() => {
      editor.markHistoryStoppingPoint('before-remove-conversation-pin');
      editor.deleteShape(shapeId);
    });
  });
  await flushBoardDocumentOps(editor, before);
}

/**
 * Delete selected canvas pins.
 * User pins: full cascade delete (activity, comment, screenshots, related shapes).
 * AI pins: remove canvas-only suggest / conversation pin shapes.
 * @returns {Promise<boolean>} false if the user cancelled confirmation
 */
export async function deleteCanvasPins(
  editor,
  pinShapes,
  { confirmUserPins = true, enqueueDigest = true } = {}
) {
  if (!editor || !pinShapes?.length) return true;

  const store = useBoardStore.getState();

  const userActivityIds = [
    ...new Set(
      pinShapes
        .filter(shape => shape.type === 'pin-card')
        .map(shape => shape.meta?.activityId)
        .filter(Boolean)
        .map(String)
    ),
  ];
  const conversationShapeIds = pinShapes
    .filter(shape => shape.type === CONVERSATION_PIN_TYPE)
    .map(shape => shape.id)
    .filter(Boolean);

  const doDelete = async () => {
    if (userActivityIds.length) {
      await deleteUserPins(editor, userActivityIds, {
        deleteActivities: store.deleteActivities,
        activities: store.activities,
        board: store.board,
        updatePlacementsLocal: store.updatePlacementsLocal,
        confirm: false,
        onDeleted: deletedIds => {
          if (!enqueueDigest) return;
          enqueueDigestDeleteFromStore(deletedIds, conversationShapeIds);
        },
      });
    } else if (enqueueDigest && conversationShapeIds.length) {
      enqueueDigestDeleteFromStore([], conversationShapeIds);
    }

    for (const shape of pinShapes) {
      if (shape.type === 'suggest-pin') {
        removeSuggestPinShape(editor, shape.id);
      } else if (shape.type === CONVERSATION_PIN_TYPE) {
        await removeConversationPinShape(editor, shape.id);
      }
    }

    syncLocalBoardSnapshot(editor);
    editor.menus.clearOpenMenus();
  };

  if (confirmUserPins && userActivityIds.length) {
    const confirmed = await confirmDeleteUserPins(userActivityIds.length, doDelete);
    if (!confirmed) return false;
    return true;
  }

  await doDelete();
  return true;
}

/**
 * Delete canvas shapes (selection, eraser, or an explicit list).
 * Frames cascade to every object inside them (including page-level user pins).
 * User pins use full cascade delete; AI pins and stock shapes are removed from the canvas.
 * Multiple digest sources are revoked in one `/digest-event` POST.
 * Persistence is object-level: /ops removes + frames-ops (no full snapshot PATCH).
 */
export async function deleteCanvasShapes(editor, shapes, { confirmUserPins = true } = {}) {
  if (!editor || !shapes?.length) return;

  const targets = expandSelectionForCanvasDelete(editor, shapes);
  const pinShapes = targets.filter(isPinShape);
  const otherShapes = targets.filter(shape => !isPinShape(shape));
  const otherShapeIds = otherShapes.map(shape => shape.id);
  const userPinCount = pinShapes.filter(shape => shape.type === 'pin-card').length;
  const digestTargets = collectDigestDeleteTargets(targets);

  const doWork = async () => {
    await withDigestDeleteListenersSuppressed(async () => {
      if (pinShapes.length) {
        const ok = await deleteCanvasPins(editor, pinShapes, {
          confirmUserPins: false,
          enqueueDigest: false,
        });
        if (!ok) return;
      }

      if (otherShapeIds.length) {
        const store = useBoardStore.getState();
        const saveFrames = buildSaveFrames();
        const removed = {};
        for (const shape of otherShapes) {
          removed[shape.id] = shape;
        }

        unmountShapesThen(otherShapeIds, () => {
          editor.run(() => {
            editor.markHistoryStoppingPoint('delete');
            editor.deleteShapes(otherShapeIds);
          });
        });

        syncLocalBoardSnapshot(editor);

        const ops = buildBoardDocumentOps({ removed, added: {}, updated: {} });
        if (ops.length) {
          try {
            await store.saveDocumentOps(ops);
          } catch {
            /* ignore */
          }
        }
        try {
          await saveFrames?.(framesFromEditor(editor));
        } catch {
          /* ignore */
        }
      }
    });

    const { activityIds, shapeIds } = digestTargets;
    if (activityIds.length || shapeIds.length) {
      enqueueDigestDeleteFromStore(activityIds, shapeIds);
    }

    editor.menus.clearOpenMenus();
  };

  // Confirm once for any selection that includes permanent user-pin deletes
  // (including pins inside a selected frame). The actual work runs as
  // `onConfirm` so the dialog shows a loading spinner.
  if (confirmUserPins && userPinCount > 0) {
    const confirmed = await confirmDeleteUserPins(userPinCount, doWork);
    if (!confirmed) return;
    return;
  }

  await doWork();
}

export async function deleteSelectedCanvasShapes(editor, options) {
  if (!editor) return;
  return deleteCanvasShapes(editor, editor.getSelectedShapes(), options);
}

function getErasingShapes(editor) {
  if (typeof editor.getErasingShapes === 'function') {
    return editor.getErasingShapes().filter(Boolean);
  }
  const ids = editor.getErasingShapeIds?.() || [];
  return ids.map(id => editor.getShape(id)).filter(Boolean);
}

function patchEraserComplete(editor, path, { canDelete } = {}) {
  const node = editor.getStateDescendant?.(path);
  if (!node || typeof node.complete !== 'function') return () => {};

  const original = node.complete;
  node.complete = function complete() {
    const shapes = getErasingShapes(editor);
    if (!shapes.length) return original.call(this);
    if (editor.getIsReadonly?.() || editor.getInstanceState?.()?.isReadonly) {
      return original.call(this);
    }
    if (typeof canDelete === 'function' && !canDelete()) {
      return original.call(this);
    }

    // Skip tldraw's native deleteShapes so frame cascade, pin confirm, digest,
    // and document-ops persist match select → delete.
    this.parent.transition('idle');
    deleteCanvasShapes(editor, shapes, { confirmUserPins: true }).catch(err => {
      console.warn('Failed to delete erased canvas shapes', err);
    });
  };

  return () => {
    node.complete = original;
  };
}

/**
 * Route tldraw eraser stroke deletes through deleteCanvasShapes so they match
 * select → delete (frame cascade, pin confirm, digest batch, document ops).
 */
export function registerEraserCanvasDelete(editor, { canDelete } = {}) {
  if (!editor?.getStateDescendant) return () => {};

  const unregisters = [
    patchEraserComplete(editor, 'eraser.erasing', { canDelete }),
    patchEraserComplete(editor, 'eraser.pointing', { canDelete }),
  ];
  return () => {
    for (const unregister of unregisters) unregister();
  };
}
